(() => {
  if (customElements.get('back-in-stock-form')) return;

  class BackInStockForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-back-in-stock-form]');
      this.emailInput = this.querySelector('[name="email"]');
      this.submitButton = this.querySelector('[type="submit"]');
      this.status = this.querySelector('[data-back-in-stock-status]');
      this.productForm = document.querySelector(`.product-form-${CSS.escape(this.dataset.sectionId)}`);

      if (!this.form || !this.emailInput || !this.submitButton || !this.productForm) return;

      try {
        const variants = JSON.parse(this.querySelector('[data-back-in-stock-variants]').textContent);
        this.variants = new Map(variants.map((variant) => [String(variant.id), variant]));
      } catch (error) {
        this.variants = new Map();
      }

      this.onSubmit = this.onSubmit.bind(this);
      this.refresh = this.refresh.bind(this);
      this.form.addEventListener('submit', this.onSubmit);
      this.productForm.addEventListener('change', () => requestAnimationFrame(this.refresh));
      document.addEventListener('product:variant-change', this.refresh);
      document.addEventListener('variant:change', this.refresh);

      this.observer = new MutationObserver(this.refresh);
      this.observer.observe(this.productForm, {
        attributes: true,
        subtree: true,
        attributeFilter: ['disabled', 'value', 'selected', 'data-current-product-id'],
      });

      this.refresh();
    }

    disconnectedCallback() {
      this.form?.removeEventListener('submit', this.onSubmit);
      document.removeEventListener('product:variant-change', this.refresh);
      document.removeEventListener('variant:change', this.refresh);
      this.observer?.disconnect();
    }

    getVariantId() {
      const variantSelect = this.productForm.querySelector('[data-variant-select]');
      return String(variantSelect?.value || this.productForm.dataset.currentProductId || this.dataset.currentVariantId || '');
    }

    refresh() {
      const variantId = this.getVariantId();
      const variant = this.variants.get(variantId);
      const addToCartButton = this.productForm.querySelector('[data-add-to-cart]');
      const unavailable = variant ? !variant.available : Boolean(addToCartButton?.disabled);

      this.dataset.currentVariantId = variantId;
      this.hidden = !variantId || !unavailable;
      this.clearStatus();
    }

    clearStatus() {
      if (!this.status) return;
      this.status.textContent = '';
      this.status.classList.remove('is-error');
    }

    setStatus(message, isError = false) {
      this.status.textContent = message;
      this.status.classList.toggle('is-error', isError);
    }

    async onSubmit(event) {
      event.preventDefault();
      this.clearStatus();

      if (!this.emailInput.checkValidity()) {
        this.emailInput.reportValidity();
        return;
      }

      const variantId = this.getVariantId();
      if (!variantId || !this.variants.has(variantId)) {
        this.setStatus('Please select an unavailable variant and try again.', true);
        return;
      }

      this.submitButton.disabled = true;
      this.setAttribute('aria-busy', 'true');

      try {
        const response = await fetch(
          `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${encodeURIComponent(this.dataset.companyId)}`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              revision: '2024-06-15',
            },
            body: JSON.stringify({
              data: {
                type: 'back-in-stock-subscription',
                attributes: {
                  profile: {
                    data: {
                      type: 'profile',
                      attributes: { email: this.emailInput.value.trim() },
                    },
                  },
                  channels: ['EMAIL'],
                },
                relationships: {
                  variant: {
                    data: {
                      type: 'catalog-variant',
                      id: `$shopify:::$default:::${variantId}`,
                    },
                  },
                },
              },
            }),
          }
        );

        if (!response.ok) {
          let message = 'We could not save your request. Please try again.';
          try {
            const result = await response.json();
            message = result.errors?.[0]?.detail || message;
          } catch (error) {
            // Keep the friendly fallback message when the API returns no JSON.
          }
          throw new Error(message);
        }

        this.form.reset();
        this.setStatus('We will notify you when this item is back in stock.');
      } catch (error) {
        this.setStatus(error.message || 'We could not save your request. Please try again.', true);
      } finally {
        this.submitButton.disabled = false;
        this.removeAttribute('aria-busy');
      }
    }
  }

  customElements.define('back-in-stock-form', BackInStockForm);
})();
